/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { formatDate } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EvaluatorForStudent, JobDetails, StudentForEvaluator } from '@app/app/page-modules/admin/components/mentors/admin-mentors.model';
import {
  CreateJobRequest,
  MENTOR_MENTEE_DATA_SERVICE,
  MentorMenteeDataService,
} from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';

export interface ManageJobsDialogData {
  user: StudentForEvaluator | EvaluatorForStudent;
  evaluatorId: string;
  studentId: string;
}

@Component({
  selector: 'ptl-manage-jobs-dialog',
  templateUrl: './manage-jobs-dialog.component.html',
  styleUrls: ['./manage-jobs-dialog.component.scss'],
})
export class ManageJobsDialogComponent implements OnInit {
  userJobs: JobDetails[] = [];
  isLoading = false;
  showCreateForm = false;
  jobForm: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ManageJobsDialogData,
    @Inject(MENTOR_MENTEE_DATA_SERVICE) private mentorMenteeService: MentorMenteeDataService,
    @Inject(TOAST_NOTIFICATION_SERVICE) private toastService: ToastService,
    private dialogRef: MatDialogRef<ManageJobsDialogComponent>,
    private fb: FormBuilder,
  ) {
    this.jobForm = this.fb.group({
      title: ['', Validators.required],
      level: ['', Validators.required],
      startDate: [null, Validators.required],
      endDate: [null],
    });
  }

  ngOnInit(): void {
    this.userJobs = [...(this.data.user.jobs || [])];
  }

  onCreateJob(): void {
    if (this.jobForm.invalid) {
      return;
    }

    this.isLoading = true;
    const formValue = this.jobForm.value;

    const request: CreateJobRequest = {
      title: formValue.title,
      level: formValue.level,
      startDate: this.formatDate(formValue.startDate),
      endDate: formValue.endDate ? this.formatDate(formValue.endDate) : undefined,
      evaluatorUserUid: this.data.evaluatorId,
      studentUserUid: this.data.studentId,
    };

    this.mentorMenteeService.createJob(request).subscribe((result) => {
      if (result.isSuccess) {
        this.userJobs.push(result.value);
        this.jobForm.reset();
        this.showCreateForm = false;
        this.toastService.showSuccess('Job created successfully');
      } else {
        this.toastService.showFail('Failed to create job');
      }
      this.isLoading = false;
    });
  }

  onRemoveJob(jobId: string): void {
    this.isLoading = true;
    this.mentorMenteeService.removeJobFromStudentEvaluator(this.data.evaluatorId, this.data.studentId, jobId).subscribe((result) => {
      if (result.isSuccess) {
        this.userJobs = this.userJobs.filter((job) => job._id !== jobId);
        this.toastService.showSuccess('Job removed successfully');
      } else {
        this.toastService.showFail('Failed to remove job');
      }
      this.isLoading = false;
    });
  }

  isJobActive(job: JobDetails): boolean {
    if (!job.endDate) {
      return true;
    }
    const today = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    return job.endDate >= today;
  }

  close(): void {
    this.dialogRef.close(this.userJobs);
  }

  private formatDate(date: Date): string {
    return formatDate(date, 'yyyy-MM-dd', 'en');
  }
}
